import { useState, useEffect } from "react"
import SlotPicker from "./SlotPicker"
import { Alert } from "@ui/Alert"
import { Card } from "@ui/Card"
import { Input } from "@ui/Input"
import { Select } from "@ui/Select"

export default function StudentView({ courseId }) {
  const [ instructors, setInstructors ] = useState([])
  const [ selectedInstructor, setSelectedInstructor ] = useState(null)
  const [ selectedDate, setSelectedDate ] = useState("")
  const [ slots, setSlots ] = useState([])
  const [ loadingSlots, setLoadingSlots ] = useState(false)
  const [ booking, setBooking ] = useState(false)
  const [ message, setMessage ] = useState(null)

  useEffect(() => {
    fetchInstructors()
  }, [])

  useEffect(() => {
    if (selectedInstructor && selectedDate) {
      fetchSlots()
    } else {
      setSlots([])
    }
  }, [ selectedInstructor, selectedDate ])

  async function fetchInstructors() {
    try {
      const res = await fetch(`/lti/courses/${courseId}/office_hours_bookings/instructors`, {
        headers: { "Accept": "application/json" }
      })
      if (res.ok) {
        const data = await res.json()
        setInstructors(data.instructors)
        if (data.instructors.length === 1) {
          setSelectedInstructor(data.instructors[0])
        }
      }
    } catch {
      // ignore
    }
  }

  async function fetchSlots() {
    setLoadingSlots(true)
    try {
      const res = await fetch(
        `/lti/courses/${courseId}/office_hours_bookings/available_slots?office_hour_id=${selectedInstructor.id}&date=${selectedDate}`,
        { headers: { "Accept": "application/json" } }
      )
      if (res.ok) {
        const data = await res.json()
        setSlots(data.slots)
      }
    } catch {
      // ignore
    } finally {
      setLoadingSlots(false)
    }
  }

  async function handleBook(slot) {
    setBooking(true)
    setMessage(null)

    try {
      const res = await fetch(`/lti/courses/${courseId}/office_hours_bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          office_hour_id: selectedInstructor.id,
          starts_at: slot.starts_at
        })
      })
      if (res.ok) {
        setMessage({ type: "success", text: `Booked! ${slot.start_time} - ${slot.end_time}` })
        fetchSlots() // Refresh to remove booked slot
      } else {
        const data = await res.json()
        setMessage({ type: "error", text: data.error || "Failed to book" })
      }
    } catch {
      setMessage({ type: "error", text: "Network error" })
    } finally {
      setBooking(false)
    }
  }

  if (instructors.length === 0) {
    return <p className="text-sm text-webex-gray-600 dark:text-webex-gray-400">No instructors have set up office hours for this course yet.</p>
  }

  return (
    <div className="space-y-4">
      {instructors.length > 1 && (
        <Card padding="md">
          <label className="text-sm font-semibold text-webex-gray-1000 dark:text-white block mb-2">Select instructor</label>
          <Select
            value={selectedInstructor?.id || ""}
            onChange={(e) => {
              const instr = instructors.find(i => i.id === parseInt(e.target.value))
              setSelectedInstructor(instr || null)
            }}
          >
            <option value="">Choose an instructor...</option>
            {instructors.map(i => (
              <option key={i.id} value={i.id}>{i.instructor_name}</option>
            ))}
          </Select>
        </Card>
      )}

      {selectedInstructor && (
        <>
          {instructors.length === 1 && (
            <Card padding="md">
              <p className="text-sm text-webex-gray-700 dark:text-webex-gray-300">
                Booking office hours with <span className="font-medium text-webex-gray-1000 dark:text-white">{selectedInstructor.instructor_name}</span>
              </p>
            </Card>
          )}

          <Card padding="md">
            <label className="text-sm font-semibold text-webex-gray-1000 dark:text-white block mb-2">Select date</label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
            />
          </Card>

          {selectedDate && (
            <Card padding="md">
              <h3 className="text-sm font-semibold text-webex-gray-1000 dark:text-white mb-3">Available times</h3>
              {loadingSlots ? (
                <p className="text-sm text-webex-gray-600 dark:text-webex-gray-400">Loading available times...</p>
              ) : (
                <SlotPicker slots={slots} onBook={handleBook} booking={booking} />
              )}
            </Card>
          )}

          {message && (
            <Alert variant={message.type === "success" ? "success" : "error"}>
              <Alert.Icon />
              <Alert.Content>
                <Alert.Description>{message.text}</Alert.Description>
              </Alert.Content>
            </Alert>
          )}
        </>
      )}
    </div>
  )
}
